import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  FormSizes,
  Button,
  ButtonVariants,
  ButtonSizes,
  FormValidateOn,
} from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { notification } from '@/lib/notifications';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { EmptyState, LoadingOverlay } from '@/components/feedback';
import { ROUTES } from '@/app/routes';
import {
  useGetDeclarationQuery,
  useValidateDeclarationMutation,
  useEditDeclarationMutation,
} from '../../api/declarationsApi';
import { useValidateDeclarationFormOptions } from '../../hooks';
import { ValidateDeclarationStep } from '../../constants/validateDeclaration.constants';
import { DECLARATION_STATUS } from '../../constants/declarations.constants';
import { PdfPreviewPanel } from './PdfPreviewPanel';
import { ValidateDeclarationForm } from './ValidateDeclarationForm';
import { ValidateDeclarationSummary } from './ValidateDeclarationSummary';
import styles from './DeclarationForm.module.scss';

const TITLE_KEY_BY_MODE = Object.freeze({
  validate: 'DECLARATIONS_VALIDATE_TITLE',
  edit: 'DECLARATIONS_EDIT_TITLE',
});

/**
 * Props:
 *   mode  'validate' | 'edit' — changes the header title and which mutation Save submits to
 */
export const DeclarationForm = ({ mode = 'validate' }) => {
  const t = useTranslations();
  const navigate = useNavigate();
  const location = useLocation();
  const { module, declarationId } = useParams();

  const formOptions = useValidateDeclarationFormOptions();
  // GraphQL: account-scoped, so unlike the mutations it needs no `module`.
  const {
    data,
    isLoading: isLoadingDeclaration,
    isError,
  } = useGetDeclarationQuery({ id: declarationId }, { skip: !declarationId });
  const [validateDeclaration, { isLoading: isValidating }] = useValidateDeclarationMutation();
  const [editDeclaration, { isLoading: isEditing }] = useEditDeclarationMutation();
  const isSubmitting = isValidating || isEditing;

  const [step, setStep] = useState(ValidateDeclarationStep.validate);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  // True only while Next's validated submit is in flight — see handleSave.
  const isAdvancingRef = useRef(false);

  const steps = useMemo(
    () => [
      { label: t('DECLARATIONS_VALIDATE_STEP_VALIDATE') },
      { label: t('DECLARATIONS_STEP_SUMMARY') },
    ],
    [t],
  );

  // Opened from a shipment's Linked Declarations tab returns there instead of the list.
  const goToList = useCallback(
    () => navigate(location.state?.from ?? ROUTES.declarations(module)),
    [navigate, location.state, module],
  );

  const handleCancelDiscard = useCallback(() => setIsDiscardModalOpen(false), []);
  const isDraft = data?.statusValue === DECLARATION_STATUS.draft;
  const handleGoToValidate = useCallback(() => setStep(ValidateDeclarationStep.validate), []);

  const handleSave = useCallback(
    async (values) => {
      if (isAdvancingRef.current) {
        isAdvancingRef.current = false;
        setStep(ValidateDeclarationStep.summary);
        return;
      }
      try {
        const materials = formOptions.materials;
        const submitDeclaration = mode === 'validate' ? validateDeclaration : editDeclaration;
        await submitDeclaration({ module, id: declarationId, values, materials }).unwrap();
        notification.success(t('DECLARATIONS_SUCCESS_TOAST', { count: 1 }));
        setIsDiscardModalOpen(false);
        setTimeout(goToList, 1200);
      } catch {
        // notifyApiErrors already surfaced it.
      }
    },
    [
      mode,
      validateDeclaration,
      editDeclaration,
      module,
      declarationId,
      formOptions.materials,
      goToList,
      t,
    ],
  );

  // Discard-and-save bypasses the Next gate on purpose, so it must never read a stale flag.
  const handleSaveFromDiscard = useCallback(
    (values) => {
      isAdvancingRef.current = false;
      return handleSave(values);
    },
    [handleSave],
  );

  if (isLoadingDeclaration || formOptions.isLoading) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  if (isError || !data) {
    return (
      <EmptyState
        title={t('DECLARATIONS_LOAD_ERROR')}
        action={
          <Button variant={ButtonVariants.secondary} onClick={goToList}>
            {t('COMMON_BACK')}
          </Button>
        }
      />
    );
  }

  return (
    <Form
      validateOn={FormValidateOn.change}
      size={FormSizes.large}
      initialValues={data.values}
      onSubmit={handleSave}
      className={styles.page}
    >
      {({ setValue, values, submit }) => {
        const isDirty = JSON.stringify(values) !== JSON.stringify(data.values);
        const handleCloseClick = () => {
          if (isDirty) setIsDiscardModalOpen(true);
          else goToList();
        };
        const handleGoToSummary = () => {
          isAdvancingRef.current = true;
          submit();
        };
        return (
          <>
            <Form.Header
              title={t(TITLE_KEY_BY_MODE[mode], { referenceId: data.batchId })}
              onClose={handleCloseClick}
            />

            <div className={styles.stepperBar}>
              <Form.Stepper
                steps={steps}
                activeStep={step}
                variant="flush"
                className={styles.stepper}
              />
            </div>

            {step === ValidateDeclarationStep.validate ? (
              <div className={styles.body}>
                <PdfPreviewPanel fileUrl={data.fileUrl} />
                <ValidateDeclarationForm
                  certificateType={formOptions.certificateType}
                  formOptions={formOptions}
                  setValue={setValue}
                  locked={data.locked}
                />
              </div>
            ) : (
              <ValidateDeclarationSummary formOptions={formOptions} />
            )}

            <Form.Footer align="between">
              <Button
                type="button"
                variant={ButtonVariants.tertiary}
                size={ButtonSizes.sm}
                disabled={step === ValidateDeclarationStep.validate}
                onClick={handleGoToValidate}
              >
                {t('COMMON_PREVIOUS')}
              </Button>
              {step === ValidateDeclarationStep.validate ? (
                <div className={styles.footerRight}>
                  <Button
                    key="cancel"
                    type="button"
                    variant={ButtonVariants.neutral}
                    size={ButtonSizes.sm}
                    onClick={handleCloseClick}
                  >
                    {t('COMMON_CANCEL')}
                  </Button>
                  <Button
                    key="next"
                    type="button"
                    variant={ButtonVariants.primary}
                    size={ButtonSizes.sm}
                    onClick={handleGoToSummary}
                  >
                    {t('COMMON_NEXT')}
                  </Button>
                </div>
              ) : (
                <div className={styles.footerRight}>
                  <Button
                    key="saveAndNext"
                    type="submit"
                    variant={ButtonVariants.secondary}
                    size={ButtonSizes.sm}
                    disabled={isSubmitting}
                  >
                    {t('DECLARATIONS_SAVE_AND_VALIDATE_NEXT')}
                  </Button>
                  <Button
                    key="save"
                    type="submit"
                    variant={ButtonVariants.primary}
                    size={ButtonSizes.sm}
                    disabled={isSubmitting}
                  >
                    {t('COMMON_SAVE')}
                  </Button>
                </div>
              )}
            </Form.Footer>

            {/* Leaving is always Yes/Cancel, as everywhere else; a DRAFT can also save. */}
            <ConfirmDialog
              open={isDiscardModalOpen}
              title={t('COMMON_DISCARD_TITLE')}
              bodyTitle={t('COMMON_DISCARD_BODY_TITLE')}
              description={t('COMMON_DISCARD_DESCRIPTION')}
              confirmLabel={t('COMMON_YES')}
              onConfirm={goToList}
              extraLabel={t('DECLARATIONS_SAVE_CHANGES')}
              onExtra={isDraft ? () => handleSaveFromDiscard(values) : undefined}
              onCancel={handleCancelDiscard}
            />
          </>
        );
      }}
    </Form>
  );
};
